<template>
  <div class="formations-page">
    <!-- Hero -->
    <section class="hero">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1 data-aos="fade-down">Catalogue Formations 2026</h1>
        <p data-aos="fade-up" data-aos-delay="200">48 formations inter-entreprises + 29 themes intra-entreprise sur demande</p>
      </div>
    </section>

    <div class="container">

      <!-- Tabs Inter / Intra -->
      <section class="section-block">
        <div class="tabs-wrapper" data-aos="fade-up">
          <button :class="['tab-btn', { active: activeTab === 'inter' }]" @click="activeTab = 'inter'">
            <i class="fas fa-calendar-alt me-2"></i>Formations planifiees
          </button>
          <button :class="['tab-btn', { active: activeTab === 'intra' }]" @click="activeTab = 'intra'">
            <i class="fas fa-building me-2"></i>Formations sur demande
          </button>
        </div>

        <!-- Filtres (Inter uniquement) -->
        <div v-if="activeTab === 'inter'" class="filters-bar" data-aos="fade-up">
          <div class="filter-group">
            <label>Mois</label>
            <select v-model="selectedMonth" class="filter-select">
              <option value="">Tous les mois</option>
              <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label>Recherche</label>
            <input type="text" v-model="searchQuery" placeholder="Rechercher un theme..." class="filter-input" />
          </div>
          <button class="btn-reset" @click="resetFilters">
            <i class="fas fa-redo"></i> Reinitialiser
          </button>
        </div>

        <!-- Formations Inter -->
        <div v-if="activeTab === 'inter'">
          <div v-if="filteredFormations.length === 0" class="empty-state" data-aos="fade-up">
            <i class="fas fa-search"></i>
            <p>Aucune formation trouvee pour ces criteres.</p>
          </div>

          <div v-for="(group, monthName) in groupedFormations" :key="monthName" class="month-group" data-aos="fade-up">
            <h3 class="month-title">
              <i class="fas fa-calendar me-2"></i>{{ monthName }}
            </h3>
            <div class="formations-grid">
              <div v-for="(f, index) in group" :key="f.code" class="formation-card" :data-aos="'fade-up'" :data-aos-delay="index * 50">
                <div class="card-header-bar">
                  <span class="code-badge">{{ f.code }}</span>
                  <span :class="['status-badge', getStatusClass(f)]">{{ getStatusLabel(f) }}</span>
                </div>
                <h4>{{ f.theme }}</h4>
                <div class="card-meta">
                  <span><i class="fas fa-map-marker-alt"></i> {{ f.lieu }}</span>
                  <span><i class="fas fa-clock"></i> {{ f.periode }}</span>
                </div>
                <div class="card-pricing">
                  <span class="price">A partir de <strong>250 000 FCFA</strong> HT</span>
                </div>
                <button class="btn-details" @click="showFormationDetail(f)">
                  <i class="fas fa-info-circle me-1"></i>Voir details
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Formations Intra -->
        <div v-if="activeTab === 'intra'">
          <div class="intra-header" data-aos="fade-up">
            <div class="intra-info-box">
              <i class="fas fa-info-circle"></i>
              <div>
                <strong>Formations intra-entreprise sur demande</strong>
                <p>Toutes nos formations intra-entreprise durent 3 jours et sont dispensees dans vos locaux ou au siege du cabinet. Contactez-nous pour un devis personnalise.</p>
              </div>
            </div>
          </div>
          <div class="intra-grid">
            <div v-for="(f, index) in intraFormations" :key="f.code" class="intra-card" :data-aos="'fade-up'" :data-aos-delay="index * 30">
              <span class="code-badge small">{{ f.code }}</span>
              <h4>{{ f.theme }}</h4>
              <div class="intra-meta">
                <span><i class="fas fa-clock"></i> 3 jours</span>
                <span><i class="fas fa-users"></i> Sur demande</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Tarification -->
      <section class="section-block pricing-section" data-aos="fade-up">
        <div class="text-center mb-4">
          <div class="section-label">Tarification</div>
          <h2>Grille tarifaire degressive</h2>
        </div>
        <div class="pricing-grid">
          <div class="pricing-card" data-aos="zoom-in">
            <div class="pricing-header blue">1-2 participants</div>
            <div class="pricing-amount">350 000 <small>FCFA HT</small></div>
            <ul>
              <li><i class="fas fa-check"></i> Attestation de participation</li>
              <li><i class="fas fa-check"></i> Kit participant (stylo, bloc-notes, cartable)</li>
              <li><i class="fas fa-check"></i> Cle USB avec supports de cours</li>
              <li><i class="fas fa-check"></i> Pauses cafe et dejeuner</li>
            </ul>
          </div>
          <div class="pricing-card featured" data-aos="zoom-in" data-aos-delay="100">
            <div class="pricing-badge">Populaire</div>
            <div class="pricing-header orange">3-4 participants</div>
            <div class="pricing-amount">300 000 <small>FCFA HT</small></div>
            <ul>
              <li><i class="fas fa-check"></i> Attestation de participation</li>
              <li><i class="fas fa-check"></i> Kit participant (stylo, bloc-notes, cartable)</li>
              <li><i class="fas fa-check"></i> Cle USB avec supports de cours</li>
              <li><i class="fas fa-check"></i> Pauses cafe et dejeuner</li>
            </ul>
          </div>
          <div class="pricing-card" data-aos="zoom-in" data-aos-delay="200">
            <div class="pricing-header blue">5-7 participants</div>
            <div class="pricing-amount">250 000 <small>FCFA HT</small></div>
            <ul>
              <li><i class="fas fa-check"></i> Attestation de participation</li>
              <li><i class="fas fa-check"></i> Kit participant (stylo, bloc-notes, cartable)</li>
              <li><i class="fas fa-check"></i> Cle USB avec supports de cours</li>
              <li><i class="fas fa-check"></i> Pauses cafe et dejeuner</li>
            </ul>
          </div>
        </div>
        <p class="pricing-note text-center mt-3">
          <i class="fas fa-info-circle me-1"></i>
          Paiement 100% a l'inscription. Modes : cheque, virement ou espece (NSIA Bank).
          Deplacement, hebergement et diner non inclus.
        </p>
      </section>

      <!-- CTA Contact -->
      <section class="section-block" data-aos="fade-up">
        <div class="cta-box">
          <h2>Inscrivez-vous a nos prochaines formations</h2>
          <p>Contactez-nous par email ou telephone pour reserver vos places</p>
          <div class="cta-actions">
            <a href="mailto:prefereplus@gmail.com?subject=Inscription Formation" class="btn btn-cta">
              <i class="fas fa-envelope me-2"></i>prefereplus@gmail.com
            </a>
            <a href="tel:+22901401188884" class="btn btn-cta-outline">
              <i class="fas fa-phone-alt me-2"></i>01 40 11 88 84
            </a>
          </div>
        </div>
      </section>
    </div>

    <!-- Modal Detail Formation -->
    <div class="modal fade" id="formationDetailModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content" v-if="selectedFormation">
          <div class="modal-header">
            <div>
              <span class="code-badge">{{ selectedFormation.code }}</span>
              <h5 class="modal-title mt-2">{{ selectedFormation.theme }}</h5>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="detail-grid">
              <div class="detail-item">
                <i class="fas fa-map-marker-alt"></i>
                <div><strong>Lieu</strong><br>{{ selectedFormation.lieu }}</div>
              </div>
              <div class="detail-item">
                <i class="fas fa-calendar"></i>
                <div><strong>Periode</strong><br>{{ selectedFormation.periode }}</div>
              </div>
              <div class="detail-item">
                <i class="fas fa-money-bill-wave"></i>
                <div><strong>Tarif</strong><br>A partir de 250 000 FCFA HT</div>
              </div>
              <div class="detail-item">
                <i class="fas fa-user-tie"></i>
                <div><strong>Formateurs</strong><br>Formateurs seniors (+20 ans exp.)</div>
              </div>
            </div>
            <div class="mt-3 p-3" style="background: #f8f9fa; border-radius: 12px;">
              <h6><i class="fas fa-gift me-2 text-primary"></i>Les plus de la formation</h6>
              <ul class="mb-0 ps-3" style="font-size: 0.9rem; color: #4a5568;">
                <li>Sessions participatives, echanges et exercices pratiques</li>
                <li>Cle USB avec videos et exercices</li>
                <li>Suivi telephonique sur un mois apres la formation</li>
                <li>Attestation de participation</li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <a :href="'mailto:prefereplus@gmail.com?subject=Inscription ' + selectedFormation.code + ' - ' + selectedFormation.theme" class="btn btn-cta">
              <i class="fas fa-envelope me-2"></i>S'inscrire par email
            </a>
            <a href="tel:+22901401188884" class="btn btn-cta-outline">
              <i class="fas fa-phone-alt me-2"></i>Appeler
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";

export default {
  name: "FormationsPage",
  data() {
    return {
      activeTab: "inter",
      selectedMonth: "",
      searchQuery: "",
      selectedFormation: null,
      loading: false,
      apiLoaded: false,
      months: [
        { value: "Janvier", label: "Janvier 2026" },
        { value: "Fevrier", label: "Fevrier 2026" },
        { value: "Mars", label: "Mars 2026" },
        { value: "Avril", label: "Avril 2026" },
        { value: "Mai", label: "Mai 2026" },
        { value: "Juin", label: "Juin 2026" },
        { value: "Juillet", label: "Juillet 2026" },
        { value: "Aout", label: "Aout 2026" },
        { value: "Septembre", label: "Septembre 2026" },
        { value: "Octobre", label: "Octobre 2026" },
        { value: "Novembre", label: "Novembre 2026" },
        { value: "Decembre", label: "Decembre 2026" },
      ],
      interFormations: [
        { code: "2026-F-JN-1", theme: "Techniques fiscales : Avantages et Allegement fiscal de la loi des finances 2026", lieu: "Cotonou", periode: "14 au 16 Janvier", month: "Janvier", endDate: "2026-01-16" },
        { code: "2026-F-JN-2", theme: "Secretaire et assistant de direction : ameliorer votre efficacite professionnelle", lieu: "Cotonou", periode: "19 au 21 Janvier", month: "Janvier", endDate: "2026-01-21" },
        { code: "2026-F-JN-3", theme: "La gestion des ressources humaines pour les managers", lieu: "Cotonou", periode: "26 au 28 Janvier", month: "Janvier", endDate: "2026-01-28" },
        { code: "2026-F-JN-4", theme: "La gestion du stress au travail", lieu: "Cotonou", periode: "28 au 30 Janvier", month: "Janvier", endDate: "2026-01-30" },
        { code: "2026-F-FV-1", theme: "Le recrutement a l'ere de la digitalisation", lieu: "Cotonou", periode: "03 au 05 Fevrier", month: "Fevrier", endDate: "2026-02-05" },
        { code: "2026-F-FV-2", theme: "Ingenierie de la formation : de l'analyse des besoins a la conception d'un programme", lieu: "Cotonou", periode: "11 au 13 Fevrier", month: "Fevrier", endDate: "2026-02-13" },
        { code: "2026-F-FV-3", theme: "Impact de la transformation digitale dans la fonction secretaire et Assistant de direction", lieu: "Cotonou", periode: "16, 17 et 19 Fevrier", month: "Fevrier", endDate: "2026-02-19" },
        { code: "2026-F-FV-4", theme: "Roles et responsabilites des delegues du personnel et membres du CHSSET", lieu: "Cotonou", periode: "25 au 27 Fevrier", month: "Fevrier", endDate: "2026-02-27" },
        { code: "2026-F-MR-1", theme: "La gestion des ressources humaines en entreprise : tout pour le cadre en reconversion", lieu: "Cotonou", periode: "03 au 05 Mars", month: "Mars", endDate: "2026-03-05" },
        { code: "2026-F-MR-2", theme: "Planification des effectifs et recrutement", lieu: "Cotonou", periode: "10 au 12 Mars", month: "Mars", endDate: "2026-03-12" },
        { code: "2026-F-MR-3", theme: "Le comite d'hygiene, de sante, de securite et de l'environnement au travail (CHSSET)", lieu: "Cotonou", periode: "17 au 19 Mars", month: "Mars", endDate: "2026-03-19" },
        { code: "2026-F-MR-4", theme: "Suivi et evaluation des projets programmes de developpement", lieu: "Cotonou", periode: "24 au 26 Mars", month: "Mars", endDate: "2026-03-26" },
        { code: "2026-F-AV-1", theme: "Techniques d'accueil des usagers et qualite de service", lieu: "Cotonou", periode: "08 au 10 Avril", month: "Avril", endDate: "2026-04-10" },
        { code: "2026-F-AV-2", theme: "Gestion efficace des reunions", lieu: "Cotonou", periode: "15 au 17 Avril", month: "Avril", endDate: "2026-04-17" },
        { code: "2026-F-AV-3", theme: "Sauveteur et secourisme au travail", lieu: "Cotonou", periode: "22 au 24 Avril", month: "Avril", endDate: "2026-04-24" },
        { code: "2026-F-AV-4", theme: "Safety/Securite", lieu: "Cotonou", periode: "27 au 29 Avril", month: "Avril", endDate: "2026-04-29" },
        { code: "2026-F-MA-1", theme: "Developpement des competences et gestion de la performance des employes", lieu: "Cotonou", periode: "05 au 07 Mai", month: "Mai", endDate: "2026-05-07" },
        { code: "2026-F-MA-2", theme: "Passation des marches publics : Procedures, Outils et Techniques", lieu: "Cotonou", periode: "11 au 13 Mai", month: "Mai", endDate: "2026-05-13" },
        { code: "2026-F-MA-3", theme: "ISO 27001 : Lead Auditor avec Certification", lieu: "Cotonou", periode: "18 au 20 Mai", month: "Mai", endDate: "2026-05-20" },
        { code: "2026-F-MA-4", theme: "Le digital au service des professionnels RH : tableaux de bord RH automatises", lieu: "Cotonou", periode: "22, 28 et 29 Mai", month: "Mai", endDate: "2026-05-29" },
        { code: "2026-F-JN-1b", theme: "Marches publics et maitrise des procedures des bailleurs de fonds (BAD, Banque Mondiale, FMI)", lieu: "Cotonou", periode: "03 au 05 Juin", month: "Juin", endDate: "2026-06-05" },
        { code: "2026-F-JN-2b", theme: "Sage paie i7 pour professionnel RH", lieu: "Cotonou", periode: "09 au 11 Juin", month: "Juin", endDate: "2026-06-11" },
        { code: "2026-F-JN-3b", theme: "Fiscalite d'entreprise de BTP", lieu: "Cotonou", periode: "17 au 19 Juin", month: "Juin", endDate: "2026-06-19" },
        { code: "2026-F-JN-4b", theme: "Gestion d'entrepot et de stock sur chantier", lieu: "Cotonou", periode: "24 au 26 Juin", month: "Juin", endDate: "2026-06-26" },
        { code: "2026-F-JL-1", theme: "Gestion des chaines d'approvisionnement", lieu: "Cotonou", periode: "07 au 09 Juillet", month: "Juillet", endDate: "2026-07-09" },
        { code: "2026-F-JL-2", theme: "Leadership et performance strategique", lieu: "Cotonou", periode: "15 au 17 Juillet", month: "Juillet", endDate: "2026-07-17" },
        { code: "2026-F-JL-3", theme: "Gestion des carrieres, des performances et de la mobilite", lieu: "Cotonou", periode: "21 au 23 Juillet", month: "Juillet", endDate: "2026-07-23" },
        { code: "2026-F-JL-4", theme: "Mieux securiser son systeme d'information (SECNUM)", lieu: "Cotonou", periode: "28 au 30 Juillet", month: "Juillet", endDate: "2026-07-30" },
        { code: "2026-F-AT-1", theme: "Batir une GPEC performante et operationnelle : Methodes et Outils", lieu: "Cotonou", periode: "05 au 07 Aout", month: "Aout", endDate: "2026-08-07" },
        { code: "2026-F-AT-2", theme: "Management et gestion d'une equipe : Methodes et Outils de travail", lieu: "Cotonou", periode: "11 au 13 Aout", month: "Aout", endDate: "2026-08-13" },
        { code: "2026-F-AT-3", theme: "E-GRH et transformation digitale", lieu: "Cotonou", periode: "19 au 21 Aout", month: "Aout", endDate: "2026-08-21" },
        { code: "2026-F-AT-4", theme: "Excel avance pour l'Analyse Financiere et le controle de Gestion", lieu: "Cotonou", periode: "25, 27 et 29 Aout", month: "Aout", endDate: "2026-08-29" },
        { code: "2026-F-SP-1", theme: "Planification et gestion axee sur le resultat (GAR)", lieu: "Cotonou", periode: "02 au 04 Septembre", month: "Septembre", endDate: "2026-09-04" },
        { code: "2026-F-SP-2", theme: "Gestion administrative des ressources humaines et de la paie", lieu: "Cotonou", periode: "08 au 10 Septembre", month: "Septembre", endDate: "2026-09-10" },
        { code: "2026-F-SP-3", theme: "Comptabilisation des immobilisations par Composantes", lieu: "Cotonou", periode: "16 au 18 Septembre", month: "Septembre", endDate: "2026-09-18" },
        { code: "2026-F-SP-4", theme: "Techniques d'archivage physique et numerique des documents", lieu: "Cotonou", periode: "23 au 25 Septembre", month: "Septembre", endDate: "2026-09-25" },
        { code: "2026-F-OC-1", theme: "Gestion des carrieres : techniques de gestion RH et systeme de gestion des performances", lieu: "Cotonou", periode: "07 au 09 Octobre", month: "Octobre", endDate: "2026-10-09" },
        { code: "2026-F-OC-2", theme: "Devenir un(e) assistant(e) RH confirme(e) : techniques et outils modernes", lieu: "Cotonou", periode: "14 au 16 Octobre", month: "Octobre", endDate: "2026-10-16" },
        { code: "2026-F-OC-3", theme: "ISO 26000 Management de la responsabilite societale", lieu: "Cotonou", periode: "21 au 23 Octobre", month: "Octobre", endDate: "2026-10-23" },
        { code: "2026-F-OC-4", theme: "Formation UI/UX Design", lieu: "Cotonou", periode: "28 au 30 Octobre", month: "Octobre", endDate: "2026-10-30" },
        { code: "2026-F-NV-1", theme: "La gestion des ressources humaines pour managers", lieu: "Cotonou", periode: "04 au 06 Novembre", month: "Novembre", endDate: "2026-11-06" },
        { code: "2026-F-NV-2", theme: "Maitriser le droit des societes OHADA : gestion juridique efficace", lieu: "Cotonou", periode: "10 au 12 Novembre", month: "Novembre", endDate: "2026-11-12" },
        { code: "2026-F-NV-3", theme: "Devenir un business analyst confirme", lieu: "Cotonou", periode: "18 au 20 Novembre", month: "Novembre", endDate: "2026-11-20" },
        { code: "2026-F-NV-4", theme: "Maitriser les fondamentaux de l'andragogie : formations adaptees aux adultes", lieu: "Cotonou", periode: "25 au 27 Novembre", month: "Novembre", endDate: "2026-11-27" },
        { code: "2026-F-DC-1", theme: "La gestion des RH pour cadres et managers : techniques et outils modernes (Usage IA dans la GRH)", lieu: "Cotonou", periode: "02 au 04 Decembre", month: "Decembre", endDate: "2026-12-04" },
        { code: "2026-F-DC-2", theme: "Elaboration des etats financiers annuels : outils et demarche", lieu: "Cotonou", periode: "08 au 10 Decembre", month: "Decembre", endDate: "2026-12-10" },
        { code: "2026-F-DC-3", theme: "Le droit de travail beninois : de l'embauche a la rupture du contrat de travail", lieu: "Cotonou", periode: "14 au 16 Decembre", month: "Decembre", endDate: "2026-12-16" },
        { code: "2026-F-DC-4", theme: "Leadership et Team Building", lieu: "Cotonou", periode: "21 au 23 Decembre", month: "Decembre", endDate: "2026-12-23" },
      ],
      intraFormations: [
        { code: "2026-F-SD-1", theme: "Devenez manager 360" },
        { code: "2026-F-SD-2", theme: "Deontologie administrative et regles d'ethique" },
        { code: "2026-F-SD-3", theme: "Finances pour non-financier" },
        { code: "2026-F-SD-4", theme: "Gestion du temps en milieu professionnel" },
        { code: "2026-F-SD-5", theme: "Elaboration, mise en oeuvre et evaluation d'un plan organique et plan de formation" },
        { code: "2026-F-SD-6", theme: "Leadership et management dans l'administration publique" },
        { code: "2026-F-SD-7", theme: "Leadership et responsabilite d'un DAF ou chef service RH" },
        { code: "2026-F-SD-8", theme: "Conception et animation de systeme de suivi evaluation" },
        { code: "2026-F-SD-9", theme: "Technique d'elaboration du plan d'affaire d'une entreprise" },
        { code: "2026-F-SD-10", theme: "Techniques de planification, programmation et budgetisation des projets" },
        { code: "2026-F-SD-11", theme: "Conduite de dialogue social en administration publique" },
        { code: "2026-F-SD-12", theme: "Cohesion et esprit d'equipe pour la performance en entreprise" },
        { code: "2026-F-SD-13", theme: "Fixation d'objectifs et Evaluations des Performances" },
        { code: "2026-F-SD-14", theme: "Integration du genre dans la gestion des projets programmes de developpement" },
        { code: "2026-F-SD-15", theme: "Innovations et implications juridiques des nouvelles normes en droit de travail beninois" },
        { code: "2026-F-SD-16", theme: "Microsoft Excel avance / Excel intermediaire" },
        { code: "2026-F-SD-17", theme: "Techniques de gestion des appels telephoniques et relation client" },
        { code: "2026-F-SD-18", theme: "Reperer et developper les Potentiels et Talents" },
        { code: "2026-F-SD-19", theme: "Initiation, recyclage ou perfectionnement au logiciel Sage Paie" },
        { code: "2026-F-SD-20", theme: "Batir et dynamiser son reseau de vente" },
        { code: "2026-F-SD-21", theme: "Mieux communiquer pour mieux collaborer : communication en management de projet" },
        { code: "2026-F-SD-22", theme: "Techniques et outils de redaction des documents administratifs" },
        { code: "2026-F-SD-23", theme: "Maitriser ses emotions et stress pour anticiper les situations difficiles" },
        { code: "2026-F-SD-24", theme: "Managez et developpez une dynamique de performance au sein de votre equipe" },
        { code: "2026-F-SD-25", theme: "Regles et pratiques de gouvernance d'entreprise dans l'espace OHADA" },
        { code: "2026-F-SD-26", theme: "Elaboration des etats financiers annuels : outils et demarche" },
        { code: "2026-F-SD-27", theme: "Gestion environnementale et sociale des projets (EES, EIES, PGES)" },
        { code: "2026-F-SD-28", theme: "Asseoir son personal branding et identite digitale" },
        { code: "2026-F-SD-29", theme: "La Paie en entreprise : pratiques et processus pour une gestion efficace au Benin" },
      ],
    };
  },
  computed: {
    filteredFormations() {
      let result = this.interFormations;
      if (this.selectedMonth) {
        result = result.filter(f => f.month === this.selectedMonth);
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        result = result.filter(f => f.theme.toLowerCase().includes(q) || f.code.toLowerCase().includes(q));
      }
      return result;
    },
    groupedFormations() {
      const groups = {};
      const monthOrder = ["Janvier","Fevrier","Mars","Avril","Mai","Juin","Juillet","Aout","Septembre","Octobre","Novembre","Decembre"];
      this.filteredFormations.forEach(f => {
        if (!groups[f.month]) groups[f.month] = [];
        groups[f.month].push(f);
      });
      const sorted = {};
      monthOrder.forEach(m => {
        if (groups[m]) sorted[m + " 2026"] = groups[m];
      });
      return sorted;
    }
  },
  methods: {
    resetFilters() {
      this.selectedMonth = "";
      this.searchQuery = "";
    },
    getStatusClass(f) {
      const end = new Date(f.endDate);
      const now = new Date();
      if (end < now) return "past";
      const diff = (end - now) / (1000 * 60 * 60 * 24);
      if (diff <= 14) return "soon";
      return "upcoming";
    },
    getStatusLabel(f) {
      const end = new Date(f.endDate);
      const now = new Date();
      if (end < now) return "Terminee";
      const diff = (end - now) / (1000 * 60 * 60 * 24);
      if (diff <= 14) return "Bientot";
      return "A venir";
    },
    showFormationDetail(f) {
      this.selectedFormation = f;
      const modal = new window.bootstrap.Modal(document.getElementById('formationDetailModal'));
      modal.show();
    },
    async loadFromAPI() {
      try {
        const apiBase = process.env.VUE_APP_API_ENDPOINT + '/api/trainings';
        const response = await axios.get(apiBase, { baseURL: '' });
        if (response.data && response.data.data && response.data.data.trainings) {
          const trainings = response.data.data.trainings;
          const monthNames = ["Janvier","Fevrier","Mars","Avril","Mai","Juin","Juillet","Aout","Septembre","Octobre","Novembre","Decembre"];

          this.interFormations = trainings
            .filter(t => t.type === 'inter')
            .map(t => {
              const startDate = new Date(t.start_date);
              const endDate = new Date(t.end_date);
              const monthIndex = startDate.getMonth();
              const startDay = String(startDate.getDate()).padStart(2, '0');
              const endDay = String(endDate.getDate()).padStart(2, '0');
              const monthName = monthNames[monthIndex];
              return {
                id: t.id,
                code: t.code,
                theme: t.title,
                lieu: t.location,
                periode: `${startDay} au ${endDay} ${monthName}`,
                month: monthName,
                endDate: t.end_date,
                description: t.description,
                objectives: t.objectives,
                duration_days: t.duration_days,
              };
            });

          this.intraFormations = trainings
            .filter(t => t.type === 'intra')
            .map(t => ({
              id: t.id,
              code: t.code,
              theme: t.title,
              description: t.description,
            }));

          this.apiLoaded = true;
        }
      } catch (e) {
        // Fallback: donnees hardcodees deja en place
        console.log('API formations non disponible, utilisation des donnees locales');
      }
    }
  },
  mounted() {
    document.querySelector("body").style.backgroundColor = "#ffffff";
    // AOS initialise globalement dans main.js
    this.$nextTick(() => AOS.refresh());
    this.loadFromAPI();
  }
};
</script>

<style scoped>
.formations-page { font-family: 'Poppins', sans-serif; color: #2c3e50; }

.hero { background: url('../assets/img/bg-navbar.jpg') center/cover no-repeat; height: 45vh; min-height: 300px; display: flex; align-items: center; justify-content: center; text-align: center; color: white; position: relative; }
.hero-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(135deg, rgba(41,179,205,0.85), rgba(233,93,22,0.7)); z-index: 1; }
.hero-content { position: relative; z-index: 2; padding: 0 1rem; }
.hero-content h1 { font-size: 2.8rem; font-weight: 700; }
.hero-content p { font-size: 1.15rem; opacity: 0.95; }

.section-block { padding: 3rem 0; }
.section-label { display: inline-block; background: linear-gradient(135deg, #29b3cd, #1d9ed9); color: white; padding: 4px 14px; border-radius: 20px; font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 1rem; }
h2 { font-size: 2rem; font-weight: 700; color: #2c3e50; margin-bottom: 0.8rem; }

/* Tabs */
.tabs-wrapper { display: flex; gap: 0.5rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
.tab-btn { padding: 10px 24px; border: 2px solid #eef2f6; background: white; border-radius: 50px; font-weight: 600; font-size: 0.9rem; cursor: pointer; transition: all 0.3s; font-family: 'Poppins', sans-serif; color: #4a5568; }
.tab-btn.active { background: #29b3cd; color: white; border-color: #29b3cd; }
.tab-btn:hover:not(.active) { border-color: #29b3cd; color: #29b3cd; }

/* Filters */
.filters-bar { display: flex; gap: 1rem; align-items: flex-end; margin-bottom: 2rem; flex-wrap: wrap; padding: 1rem; background: #f8f9fa; border-radius: 12px; }
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-group label { font-size: 0.8rem; font-weight: 600; color: #6c757d; }
.filter-select, .filter-input { padding: 8px 14px; border: 1px solid #dee2e6; border-radius: 8px; font-size: 0.9rem; font-family: 'Poppins', sans-serif; min-width: 200px; }
.filter-select:focus, .filter-input:focus { outline: none; border-color: #29b3cd; box-shadow: 0 0 0 3px #29b3cd20; }
.btn-reset { padding: 8px 16px; background: white; border: 1px solid #dee2e6; border-radius: 8px; font-size: 0.85rem; cursor: pointer; transition: all 0.3s; font-family: 'Poppins', sans-serif; color: #6c757d; align-self: flex-end; }
.btn-reset:hover { border-color: #e95d16; color: #e95d16; }

/* Month groups */
.month-group { margin-bottom: 2rem; }
.month-title { font-size: 1.2rem; font-weight: 700; color: #29b3cd; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 2px solid #29b3cd20; }

/* Formation cards */
.formations-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1rem; }
.formation-card { background: white; border: 1px solid #eef2f6; border-radius: 14px; padding: 1.5rem; transition: all 0.3s; }
.formation-card:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(0,0,0,0.08); border-color: #29b3cd40; }

.card-header-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.8rem; }
.code-badge { background: #29b3cd15; color: #29b3cd; padding: 3px 10px; border-radius: 6px; font-size: 0.75rem; font-weight: 700; font-family: monospace; }
.code-badge.small { font-size: 0.7rem; }
.status-badge { padding: 3px 10px; border-radius: 20px; font-size: 0.7rem; font-weight: 600; }
.status-badge.upcoming { background: #28a74515; color: #28a745; }
.status-badge.soon { background: #ffc10715; color: #e6a800; }
.status-badge.past { background: #6c757d15; color: #6c757d; }

.formation-card h4 { font-size: 0.95rem; font-weight: 600; margin-bottom: 0.8rem; line-height: 1.4; }
.card-meta { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.8rem; }
.card-meta span { font-size: 0.82rem; color: #6c757d; }
.card-meta i { color: #29b3cd; width: 18px; margin-right: 4px; }
.card-pricing { margin-bottom: 1rem; }
.price { font-size: 0.85rem; color: #4a5568; }
.price strong { color: #e95d16; }

.btn-details { width: 100%; padding: 8px; background: #29b3cd10; color: #29b3cd; border: 1px solid #29b3cd30; border-radius: 8px; font-weight: 600; font-size: 0.85rem; cursor: pointer; transition: all 0.3s; font-family: 'Poppins', sans-serif; }
.btn-details:hover { background: #29b3cd; color: white; }

/* Intra */
.intra-info-box { display: flex; gap: 1rem; padding: 1.2rem; background: #29b3cd10; border-radius: 12px; border-left: 4px solid #29b3cd; margin-bottom: 2rem; }
.intra-info-box > i { font-size: 1.5rem; color: #29b3cd; margin-top: 2px; }
.intra-info-box strong { display: block; margin-bottom: 0.3rem; }
.intra-info-box p { font-size: 0.9rem; color: #6c757d; margin: 0; }

.intra-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1rem; }
.intra-card { background: white; border: 1px solid #eef2f6; border-radius: 12px; padding: 1.2rem; transition: all 0.3s; }
.intra-card:hover { border-color: #e95d16; box-shadow: 0 4px 15px rgba(0,0,0,0.06); }
.intra-card h4 { font-size: 0.9rem; font-weight: 600; margin: 0.5rem 0; }
.intra-meta { display: flex; gap: 1rem; }
.intra-meta span { font-size: 0.8rem; color: #6c757d; }
.intra-meta i { color: #e95d16; margin-right: 4px; }

/* Empty state */
.empty-state { text-align: center; padding: 3rem; color: #6c757d; }
.empty-state i { font-size: 3rem; margin-bottom: 1rem; opacity: 0.3; }

/* Pricing */
.pricing-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1.5rem; }
.pricing-card { background: white; border: 1px solid #eef2f6; border-radius: 16px; padding: 2rem; text-align: center; transition: all 0.3s; position: relative; }
.pricing-card.featured { border-color: #e95d16; box-shadow: 0 10px 30px rgba(233,93,22,0.12); transform: scale(1.02); }
.pricing-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: #e95d16; color: white; padding: 3px 16px; border-radius: 20px; font-size: 0.75rem; font-weight: 700; }
.pricing-header { padding: 8px 20px; border-radius: 8px; display: inline-block; font-weight: 700; font-size: 0.9rem; margin-bottom: 1rem; }
.pricing-header.blue { background: #29b3cd15; color: #29b3cd; }
.pricing-header.orange { background: #e95d1615; color: #e95d16; }
.pricing-amount { font-size: 2rem; font-weight: 800; color: #2c3e50; margin-bottom: 1.5rem; }
.pricing-amount small { font-size: 0.9rem; font-weight: 400; color: #6c757d; }
.pricing-card ul { list-style: none; padding: 0; text-align: left; }
.pricing-card li { padding: 6px 0; font-size: 0.85rem; color: #4a5568; }
.pricing-card li i { color: #29b3cd; margin-right: 8px; }
.pricing-note { font-size: 0.85rem; color: #6c757d; }

/* CTA */
.cta-box { text-align: center; padding: 3rem 2rem; background: linear-gradient(135deg, #29b3cd, #1d9ed9); border-radius: 20px; color: white; margin-bottom: 2rem; }
.cta-box h2 { color: white; font-size: 1.6rem; }
.cta-box p { color: rgba(255,255,255,0.9); margin-bottom: 1.5rem; }
.cta-actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
.btn-cta { background: #e95d16; color: white; padding: 12px 24px; border-radius: 50px; font-weight: 600; border: 2px solid #e95d16; transition: all 0.3s; text-decoration: none; font-size: 0.9rem; }
.btn-cta:hover { background: white; color: #e95d16; }
.btn-cta-outline { background: transparent; color: white; padding: 12px 24px; border-radius: 50px; font-weight: 600; border: 2px solid white; transition: all 0.3s; text-decoration: none; font-size: 0.9rem; }
.btn-cta-outline:hover { background: white; color: #29b3cd; }

/* Modal */
.modal-content { border-radius: 16px; border: none; }
.modal-header { border-bottom: 1px solid #eef2f6; padding: 1.5rem; }
.modal-title { font-size: 1.1rem; font-weight: 700; }
.modal-body { padding: 1.5rem; }
.modal-footer { border-top: 1px solid #eef2f6; padding: 1rem 1.5rem; }

.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.detail-item { display: flex; gap: 0.8rem; padding: 0.8rem; background: #f8f9fa; border-radius: 10px; }
.detail-item i { font-size: 1.2rem; color: #29b3cd; margin-top: 2px; }
.detail-item strong { display: block; font-size: 0.8rem; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; }

@media (max-width: 768px) {
  .hero-content h1 { font-size: 2rem; }
  .formations-grid, .intra-grid { grid-template-columns: 1fr; }
  .filters-bar { flex-direction: column; }
  .filter-select, .filter-input { min-width: auto; width: 100%; }
  .detail-grid { grid-template-columns: 1fr; }
  .pricing-card.featured { transform: none; }
  .pricing-grid { grid-template-columns: 1fr; }
}

@media (max-width: 575px) {
  .hero-content h1 { font-size: 1.5rem; }
  .hero-content p { font-size: 0.95rem; }
  .tab-btn { padding: 8px 16px; font-size: 0.85rem; }
}
</style>
